import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, Send, Loader2, Sparkles, RotateCcw } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const STARTER_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm an AI assistant for Akshay's portfolio. Ask me about his skills, experience, case studies, or how he could help your business — I'll answer instantly."
}

const SUGGESTED = [
  "What IT services does Akshay offer?",
  "Tell me about the Majnu project",
  "What's his data center experience?",
]

export default function AskAkshay() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([STARTER_MESSAGE])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const endRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' })
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [messages, open])

  const sendMessage = async (text) => {
    const msg = (text || input).trim()
    if (!msg || loading) return
    setInput('')

    const history = messages.filter(m => m.content)
    const newMessages = [...history, { role: 'user', content: msg }]
    setMessages([...newMessages, { role: 'assistant', content: '' }])
    setLoading(true)

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history: history.slice(-8) }),
      })

      if (!res.ok) throw new Error('Failed')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let fullText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const lines = decoder.decode(value).split('\n')
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.text) {
              fullText += data.text
              setMessages(prev => {
                const updated = [...prev]
                updated[updated.length - 1] = { role: 'assistant', content: fullText }
                return updated
              })
            }
            if (data.done) break
          } catch {}
        }
      }
    } catch {
      setMessages(prev => {
        const updated = [...prev]
        updated[updated.length - 1] = {
          role: 'assistant',
          content: "I'm having trouble right now. Please use the contact form to reach Akshay directly."
        }
        return updated
      })
    } finally {
      setLoading(false)
    }
  }

  const reset = () => setMessages([STARTER_MESSAGE])

  return (
    <>
      {/* Floating toggle button */}
      <motion.button
        onClick={() => setOpen(v => !v)}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        data-testid="ask-akshay-toggle"
        aria-label="Ask AI about Akshay"
        className="fixed bottom-[76px] right-4 lg:bottom-6 lg:right-6 z-[200] flex items-center gap-2.5 px-4 py-3 bg-[#111111] text-white text-sm font-semibold rounded-full shadow-float hover:bg-[#0047AB] transition-colors duration-200"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X size={15} />
            </motion.span>
          ) : (
            <motion.span key="open" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }} className="flex items-center gap-2">
              <MessageCircle size={15} />
          <span className="hidden sm:inline">Ask about Akshay</span>
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ask-akshay-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-[132px] right-4 lg:bottom-[5rem] lg:right-6 z-[199] w-[calc(100vw-2rem)] max-w-[370px] bg-white border border-[#E5E7EB] rounded-2xl shadow-float flex flex-col overflow-hidden"
            style={{ height: '460px' }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#F3F4F6] bg-[#FAFAFA] shrink-0">
              <div className="p-1.5 bg-[#EBF2FF] border border-[#0047AB]/15 rounded-lg">
                <Sparkles size={13} className="text-[#0047AB]" />
              </div>
              <div className="flex-1">
                <div className="font-sans font-semibold text-[#111111] text-sm">Ask about Akshay</div>
                <div className="font-mono text-[10px] text-[#9CA3AF] mt-0.5">Powered by Claude AI</div>
              </div>
              <button onClick={reset} aria-label="Reset conversation"
                className="p-1.5 text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6] rounded-lg transition-colors duration-150">
                <RotateCcw size={12} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm font-body leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#0047AB] text-white rounded-br-sm'
                      : 'bg-[#F3F4F6] text-[#374151] rounded-bl-sm'
                  }`}>
                    {msg.content ? msg.content : (
                      loading && i === messages.length - 1 ? (
                        <span className="flex items-center gap-1 py-0.5">
                          <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '130ms' }} />
                          <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '260ms' }} />
                        </span>
                      ) : null
                    )}
                  </div>
                </div>
              ))}

              {/* Suggestions — show only on first message */}
              {messages.length === 1 && (
                <div className="space-y-2 pt-1">
                  {SUGGESTED.map(s => (
                    <button key={s} onClick={() => sendMessage(s)}
                      className="w-full text-left px-3 py-2 text-xs font-body text-[#6B7280] border border-[#E5E7EB] rounded-xl bg-white hover:border-[#0047AB]/30 hover:text-[#0047AB] hover:bg-[#EBF2FF] transition-all duration-150">
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-[#F3F4F6] flex items-center gap-2 shrink-0">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Ask anything about Akshay..."
                disabled={loading}
                data-testid="ask-akshay-input"
                className="flex-1 bg-[#F3F4F6] rounded-xl px-3.5 py-2.5 text-sm font-body text-[#111111] placeholder:text-[#C4C9D4] focus:outline-none focus:ring-2 focus:ring-[#0047AB]/12 disabled:opacity-60 transition-all"
              />
              <button onClick={() => sendMessage()} disabled={!input.trim() || loading}
                data-testid="ask-akshay-send"
                aria-label="Send"
                className="p-2.5 bg-[#111111] text-white rounded-xl hover:bg-[#0047AB] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
